import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { Box, List, ListItem, Typography, Divider, Tooltip, Avatar } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// Distinct color palette for multiple people (must match MapMarkers.jsx)
const PERSON_COLORS = [
  '#1565c0', // Deep blue
  '#b71c1c', // Deep crimson
  '#2e7d32', // Forest green
  '#bf8040', // Muted bronze
  '#6a1b9a', // Deep purple
  '#00838f', // Deep cyan
  '#f57f17', // Deep amber
  '#c2185b', // Deep magenta
  '#4e342e', // Dark brown
  '#455a64', // Dark blue grey
];

// Generate color for a person based on their name
const getPersonColor = (personName, personMap) => {
  if (!personName) return '#1565c0';

  // If we have a person map (multiple people), use indexed colors
  if (personMap && personMap.size > 1) {
    const index = personMap.get(personName);
    return PERSON_COLORS[index % PERSON_COLORS.length];
  }

  // Single person, use default blue
  return '#1565c0';
};

/**
 * LocationListSidebar - Display list of locations with addresses and times
 */
export default function LocationListSidebar({ events, addresses, loadingAddresses, onLocationClick, selectedIndex, navigationMode }) {
  const itemRefs = useRef([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Build person name map for color coding
  const personMap = useMemo(() => {
    const uniquePersons = new Map();
    events.forEach(event => {
      const rawName = event.metadata?.personName || event.userId || 'Unknown';
      const eventPersonName = rawName.trim(); // Normalize whitespace
      if (!uniquePersons.has(eventPersonName)) {
        uniquePersons.set(eventPersonName, uniquePersons.size);
      }
    });
    return uniquePersons;
  }, [events]);

  // Handle item focus (from Tab, arrow keys, or scrollIntoView)
  const handleItemFocus = useCallback((index) => {
    onLocationClick(index);
  }, [onLocationClick]);

  // Handle keyboard navigation on each item
  const handleItemKeyDown = useCallback((e, index) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();

      let nextIndex;
      if (navigationMode === 'person') {
        // Navigate to next event with same person
        const currentEvent = events[index];
        const currentPersonName = (currentEvent.metadata?.personName || currentEvent.userId || 'Unknown').trim();

        // Find next event with same person
        nextIndex = events.findIndex((event, idx) => {
          if (idx <= index) return false;
          const personName = (event.metadata?.personName || event.userId || 'Unknown').trim();
          return personName === currentPersonName;
        });

        // If no next event with same person, wrap to first event with same person
        if (nextIndex === -1) {
          nextIndex = events.findIndex((event) => {
            const personName = (event.metadata?.personName || event.userId || 'Unknown').trim();
            return personName === currentPersonName;
          });
        }
      } else {
        // Navigate by time (sequential)
        nextIndex = Math.min(index + 1, events.length - 1);
      }

      if (nextIndex !== -1) {
        itemRefs.current[nextIndex]?.focus();
      }
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();

      let prevIndex;
      if (navigationMode === 'person') {
        // Navigate to previous event with same person
        const currentEvent = events[index];
        const currentPersonName = (currentEvent.metadata?.personName || currentEvent.userId || 'Unknown').trim();

        // Find previous event with same person (search backwards)
        prevIndex = -1;
        for (let i = index - 1; i >= 0; i--) {
          const personName = (events[i].metadata?.personName || events[i].userId || 'Unknown').trim();
          if (personName === currentPersonName) {
            prevIndex = i;
            break;
          }
        }

        // If no previous event with same person, wrap to last event with same person
        if (prevIndex === -1) {
          for (let i = events.length - 1; i > index; i--) {
            const personName = (events[i].metadata?.personName || events[i].userId || 'Unknown').trim();
            if (personName === currentPersonName) {
              prevIndex = i;
              break;
            }
          }
        }
      } else {
        // Navigate by time (sequential)
        prevIndex = Math.max(index - 1, 0);
      }

      if (prevIndex !== -1) {
        itemRefs.current[prevIndex]?.focus();
      }
    }
  }, [events, navigationMode]);

  // Auto-focus first item when sidebar appears
  useEffect(() => {
    if (itemRefs.current[0]) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        itemRefs.current[0]?.focus();
      }, 100);
    }
  }, [events.length]);

  // Focus item when selectedIndex changes (from marker click)
  useEffect(() => {
    if (selectedIndex !== null && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.focus();
    }
  }, [selectedIndex]);

  if (events.length <= 1) return null;

  return (
    <Box sx={{
      width: '25%',
      minWidth: 180,
      borderLeft: 1,
      borderColor: 'divider',
      overflow: 'auto',
      bgcolor: 'background.paper'
    }}>
      <List dense disablePadding>
        {events.map((event, index) => {
          const addressKey = `${event.location.latitude},${event.location.longitude}`;
          const address = addresses[addressKey];
          const eventTime = new Date(event.timestamp || event.createdAt);
          const rawName = event.metadata?.personName || event.userId || 'Unknown';
          const eventPersonName = rawName.trim(); // Normalize whitespace
          const markerColor = getPersonColor(eventPersonName, personMap);
          const personImage = event.metadata?.personImage;
          const matchSource = event.metadata?.matchSource; // 'face_match' or 'manual_filter'


          return (
            <React.Fragment key={event.eventId || index}>
              <Tooltip
                title={address?.formatted || 'No address available'}
                placement="left"
                arrow
                enterDelay={300}
              >
                <Box
                  ref={el => itemRefs.current[index] = el}
                  data-index={index}
                  component="button"
                  tabIndex={0}
                  sx={{
                    display: 'block',
                    width: '100%',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    py: 0.5,
                    px: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    },
                    '&:focus': {
                      bgcolor: 'action.selected',
                      outline: 'none'
                    }
                  }}
                  onClick={() => {
                    itemRefs.current[index]?.focus();
                  }}
                  onFocus={() => handleItemFocus(index)}
                  onKeyDown={(e) => handleItemKeyDown(e, index)}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%'
                  }}>
                    {/* Number badge */}
                    <Box sx={{
                      minWidth: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: markerColor,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65rem',
                      fontWeight: 'bold'
                    }}>
                      {index + 1}
                    </Box>

                    {/* Address and time */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" noWrap sx={{ fontSize: '0.75rem', lineHeight: 1.3, color: markerColor, fontWeight: 600 }}>
                        {eventPersonName}
                      </Typography>
                      <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem', lineHeight: 1.3, display: 'block' }}>
                        {loadingAddresses ? (
                          <em>Loading...</em>
                        ) : address ? (
                          address.short
                        ) : (
                          'No address'
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                        {eventTime.toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short'
                        })} {eventTime.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        })}
                      </Typography>
                    </Box>

                    {/* Person image and match type icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {personImage && (
                        <Avatar
                          src={personImage}
                          alt={eventPersonName}
                          sx={{ width: 36, height: 36 }}
                        />
                      )}
                      {matchSource === 'face_match' && (
                        <Tooltip title="AI Face Match" placement="right">
                          <AutoAwesomeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        </Tooltip>
                      )}
                      {matchSource === 'manual_filter' && (
                        <Tooltip title="Manual Match" placement="right">
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Tooltip>
              {index < events.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
}
